import { Button, IconButton } from "@mui/material";
import React, { useCallback, useContext } from "react";
import { createScopedLogger } from "../../../common";
import { isMuiPaletteColor } from "../../../common/colorUtils";
import IconComponent from "../../../common/components/IconComponent";
import { VariableNames } from "../../Constants/VariableNames";
import BundleContext from "../../Contexts/BundleContext";
import { useComponentSelection } from "../../Hooks/useComponentSelection";
import useComponentVisibility from "../../Hooks/useComponentVisibility";
import useObservedExpression from "../../Hooks/useObservedExpression";
import useObservedText from "../../Hooks/useObservedText";
import { ButtonModel, ButtonVariant } from "../../Models/ButtonModel";
import { IsDesignMode } from "../../Utils/runtimeDetection";
import IComponentProps from "../IComponentProps";
import getComponentLayout from "../Table/getComponentLayout";
import ViewComponent from "../View/ViewComponent";
import getButtonVariant from "./getButtonVariant";

type ButtonComponentProps = IComponentProps<ButtonModel>;
const logger = createScopedLogger("ButtonComponent");

const ButtonComponent: React.FC<ButtonComponentProps> = ({ component, context, componentPath = [] }) => {
  const bundle = useContext(BundleContext);

  const componentLayoutSx = getComponentLayout(component);

  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState<Record<string, any> | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(false);

  const [disabled] = useObservedExpression<boolean | undefined>(component.Disabled, context, "ButtonComponent");
  const { isVisible, hiddenSx } = useComponentVisibility(component, context, "ButtonComponent");

  const label = useObservedText(component.Label, context, "ButtonComponent");
  const iconName = useObservedText(component.IconName, context, "ButtonComponent");

  // Enable designer mode selection
  const selectionProps = useComponentSelection({ component, componentPath });

  const handleClick = async () => {
    setIsLoading(true);
    try {
      if (component.OnSelected) {
        await context.Evaluator.evaluate(component.OnSelected, context);
      }

      if (component.View?.Id) {
        let evaluatedInput: Record<string, any> = {};
        if (component.ViewInput) {
          evaluatedInput = await context.Evaluator.evaluate(component.ViewInput, context);
          setInput(evaluatedInput);
          logger.debug("clicked, evaluated input:", evaluatedInput);
        }
        setOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOutput = useCallback(
    async (output: Record<string, any>) => {
      logger.debug("received output from View:", output);

      if (component.OnViewReturned) {
        setIsLoading(true);
        try {
          const eventContext = context.CreateChild();
          eventContext.VariableScope.set(VariableNames.Output, output);
          await eventContext.Evaluator.evaluate(component.OnViewReturned, eventContext);
        } finally {
          setIsLoading(false);
        }
      }
      setOpen(false);
    },
    [context, component.OnViewReturned]
  );

  const isDisabled = !IsDesignMode && (isLoading || disabled === true);

  if (!isVisible) {
    return null;
  }

  // MUI color prop only accepts palette keys; hex/CSS values go into sx instead.
  const isMuiColor = isMuiPaletteColor(component.Color);
  const muiColor = isMuiColor ? (component.Color as any) : undefined;
  const colorSx = !isMuiColor && component.Color ? { color: component.Color } : {};

  return (
    <>
      {component.Variant === ButtonVariant.IconButton ? (
        <IconButton
          onClick={handleClick}
          disabled={isDisabled}
          loading={isLoading}
          title={component.DisabledTooltip || label}
          aria-label={component.DisabledTooltip || label}
          sx={{ ...componentLayoutSx, ...hiddenSx, ...colorSx }}
          color={muiColor}
          {...selectionProps}
        >
          <IconComponent iconName={iconName} size="small" color={component.IconColor} />
        </IconButton>
      ) : (
        <Button
          sx={{ ...componentLayoutSx, ...hiddenSx, ...colorSx }}
          color={muiColor}
          startIcon={<IconComponent iconName={iconName} color={component.IconColor} />}
          onClick={handleClick}
          disabled={isDisabled}
          variant={getButtonVariant(component.Variant)}
          loading={isLoading}
          title={component.DisabledTooltip}
          aria-label={component.DisabledTooltip}
          {...selectionProps}
        >
          {label}
        </Button>
      )}

      {component.View?.Id && open && (
        <ViewComponent
          key={`${component.View.Id}-${bundle.Id}`}
          component={bundle.ViewById[component.View.Id]}
          open={open}
          input={input}
          setOutput={handleOutput}
        />
      )}
    </>
  );
};

export default ButtonComponent;
